import { useState } from 'react'
import { useMutation, useQuery } from '@w/react-query'
import { IconButton } from '@w/ui/components/icon-button'
import { RotateCwIcon } from '@w/ui/components/icon'
import { useEventListener } from '@w/utils-fe'
import { useRouterContext } from '../router/flashcards-fe.router-bindings'
import { unknownErrorToError } from '@w/utils'
import { useUserSpaces } from '../router/authenticated-layout'
import { WorkspaceFailures } from '../dead-letter-queue/queue'
import { formatRelativeTime } from './relative-time'


function useWorkspaceSyncStatus(workspaceId: string) {
  const ctx = useRouterContext()
  const spaces = useUserSpaces()
  return useQuery(spaces.syncStatusOptions(workspaceId), ctx.queryClient).data
}

/**
 * The workspace's save/sync line (Notion: "check the sync indicator to make sure your changes are saved"), shown under the
 * workspace name (flashcards-editor-home.requirements.md 2026-11-02). A state wins over the time: Changes not saved
 * (`unsaved` = some page write failed and is not yet saved), Offline, Syncing…, Never synced; otherwise the time since the
 * last successful sync. The time is re-read on user activity (pointer, keys, window focus), re-rendering only when its text
 * changes. No timer: an idle app keeps no pending timers (no idle polling; fake-timer tests drive
 * pending timers until a promise settles), and an unattended screen needs no update.
 */
export function WorkspaceSyncLine({ workspaceId, unsaved = false, className }: { workspaceId: string; unsaved?: boolean; className?: string }) {
  const status = useWorkspaceSyncStatus(workspaceId)
  const lastSyncedAt = status?.lastSyncedAt ?? null
  const [now, setNow] = useState(() => Date.now())
  function tick() {
    const next = Date.now()
    if (lastSyncedAt !== null && syncedAgo(lastSyncedAt, next) !== syncedAgo(lastSyncedAt, now)) setNow(next)
  }
  useEventListener('pointermove', tick)
  useEventListener('keydown', tick)
  useEventListener('focus', tick)
  return <span role="status" data-testid="workspace-sync-status" className={className}>
    {unsaved ? <span data-testid="workspace-sync-unsaved">Changes not saved</span>
      : status?.offline ? 'Offline'
      : status?.pending ? 'Syncing…'
      : lastSyncedAt === null ? 'Never synced'
      : <>Synced <time data-testid="workspace-last-sync" dateTime={new Date(lastSyncedAt).toISOString()} title={new Date(lastSyncedAt).toLocaleString()}>{syncedAgo(lastSyncedAt, now)}</time></>}
  </span>
}

function syncedAgo(lastSyncedAt: number, now: number) {
  // A sync newer than the last tick reads as now, never as a future time.
  const elapsed = Math.max(0, now - lastSyncedAt)
  return elapsed < 60_000 ? 'just now' : formatRelativeTime(new Date(lastSyncedAt), new Date(lastSyncedAt + elapsed))
}

/** Sync now: one immediate round for the workspace, as on focus. */
export function WorkspaceSyncButton({ workspaceId, className }: { workspaceId: string; className?: string }) {
  const ctx = useRouterContext()
  const spaces = useUserSpaces()
  const refresh = useMutation({ mutationFn: () => spaces.trigger(workspaceId, true, ctx.tel) })
  return <IconButton data-testid="workspace-sync-refresh" className={className} variant="text" semanticRole="secondary" size="sm"
    loading={refresh.isPending} onClick={() => { refresh.mutate(null) }}>
    <RotateCwIcon />Sync now
  </IconButton>
}

/** Sync failures the user must see in the main view (halted receipt, failed sync, refused writes), never only in the sidebar. */
export function WorkspaceSyncAlerts({ workspaceId, className = '' }: { workspaceId: string; className?: string }) {
  const ctx = useRouterContext()
  const spaces = useUserSpaces()
  const status = useWorkspaceSyncStatus(workspaceId)
  const resources = useQuery(spaces.queryOptions(workspaceId), ctx.queryClient)
  const resource = resources.data?.resources[0]
  return <section aria-label="Workspace sync problems" className={`flex w-full flex-col gap-3 text-sm empty:hidden ${className}`}>
    {status?.halted ? <p role="alert" data-testid="workspace-sync-halted">Sync stopped: remote changes could not be applied ({status.halted.error}). Your own changes are still sent. Update the app to continue receiving changes.</p> : null}
    {status?.error && !status.offline && !status.halted ? <p role="alert">Workspace sync failed: {unknownErrorToError(status.error).message}</p> : null}
    {resource ? <WorkspaceFailures key={resource.connectionId} resource={resource} tel={ctx.tel} /> : null}
  </section>
}

/** Views without the sidebar header (cards, degraded page views): the sync line, Sync now and the alerts together. */
export function WorkspaceSyncStatus({ workspaceId, unsaved = false }: { workspaceId: string; unsaved?: boolean }) {
  return <section aria-label="Workspace sync status" className="flex flex-col gap-3 text-sm">
    <div className="flex items-center gap-2">
      <WorkspaceSyncLine workspaceId={workspaceId} unsaved={unsaved} />
      <WorkspaceSyncButton workspaceId={workspaceId} />
    </div>
    <WorkspaceSyncAlerts workspaceId={workspaceId} />
  </section>
}
